/**
 * Admin -> Edge Fleet -> Full scans of laptops (release 8, incremental laptop
 * scans; controller migration 0214).
 *
 * Laptop agents read only the files that changed since they last read them,
 * and every N days they read every file again as a safety net. This panel sets
 * N for the whole tenant or a business unit (the unit's value wins). Not set
 * anywhere: the agent's install value, else 7 days. 0: every scan reads every
 * file. Every change needs a reason and is audited.
 *
 * A controller before release 8 answers 404, and one without migration 0214
 * answers 503; the panel says which.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Save } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiError, listBusinessUnits, problemDetail, type BusinessUnit } from '@/lib/api';
import {
  getFullScanPolicy,
  setFullScanPolicy,
  type FullScanPolicyRow,
  type FullScanPolicyView,
} from '@/lib/full-scan-api';

const INPUT = 'glass-lite rounded px-2 py-1 bg-background/40 border border-border/40 text-xs';

/** "Every 7 days", "Every scan", "Not set". */
export function intervalText(days: number | null | undefined): string {
  if (days === null || days === undefined) return 'Not set';
  if (days === 0) return 'Every scan (incremental off)';
  return days === 1 ? 'Every day' : `Every ${days} days`;
}

/** The value typed, or why it cannot be saved. */
export function parseDays(text: string, max: number): { days: number } | { error: string } {
  const t = text.trim();
  if (!/^\d{1,3}$/.test(t)) return { error: `Give a whole number of days from 0 to ${max}.` };
  const n = Number(t);
  if (n > max) return { error: `At most ${max} days.` };
  return { days: n };
}

export function FullScanIntervalPanel(): JSX.Element {
  const [view, setView] = useState<FullScanPolicyView | null>(null);
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [scope, setScope] = useState<string>('tenant');
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const v = await getFullScanPolicy();
      setView(v);
      setUnavailable(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setUnavailable('This controller predates incremental laptop scans (release 8); laptops read every file on every scan.');
      } else if (e instanceof ApiError && e.status === 503) {
        setUnavailable('The full-scan interval needs database migration 0214 on this controller.');
      } else {
        setUnavailable(problemDetail(e, 'could not load the full-scan interval'));
      }
    }
    try {
      setUnits((await listBusinessUnits()).businessUnits);
    } catch {
      setUnits([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: Array<{ key: string; label: string; row: FullScanPolicyRow | null }> = view
    ? [
        { key: 'tenant', label: 'Whole tenant', row: view.tenant },
        ...view.businessUnits.map((r) => ({
          key: r.businessUnitId ?? '',
          label: r.businessUnitName ?? 'Business unit',
          row: r,
        })),
      ]
    : [];
  const max = view?.maxDays ?? 90;

  async function save(clear: boolean): Promise<void> {
    const parsed = clear ? { days: null } : parseDays(days, max);
    if ('error' in parsed) return setMsg(parsed.error);
    if (reason.trim().length < 3) return setMsg('Give a reason (it is kept in the audit log).');
    const label = scope === 'tenant' ? 'the whole tenant' : (units.find((u) => u.id === scope)?.name ?? 'this unit');
    if (
      clear &&
      !window.confirm(
        `Clear the full-scan interval of ${label}? Its laptops fall back to the next level (the tenant, the install value, or 7 days).`,
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await setFullScanPolicy({
        scopeType: scope === 'tenant' ? 'tenant' : 'business_unit',
        ...(scope === 'tenant' ? {} : { businessUnitId: scope }),
        fullScanEveryDays: parsed.days,
        reason: reason.trim(),
      });
      setDays('');
      setReason('');
      await load();
    } catch (e) {
      setMsg(problemDetail(e, 'could not save the interval'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassCard className="p-4 mt-4" data-testid="full-scan-interval-panel">
      <CardHeader className="p-0 mb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm">Full scans of laptops</CardTitle>
          <Button size="sm" variant="ghost" aria-label="Reload the full-scan interval" onClick={() => void load()}>
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
        <CardDescription className="text-xs">
          {view?.statement.incremental ??
            'Laptop agents read only the files that changed since they last read them, and upload only their findings.'}{' '}
          {view?.statement.full ?? ''}
        </CardDescription>
      </CardHeader>
      {unavailable ? (
        <p className="text-xs text-muted-foreground" role="status">
          {unavailable}
        </p>
      ) : !view ? (
        <p className="text-xs text-muted-foreground">
          <Loader2 className="inline h-3 w-3 animate-spin" /> Loading
        </p>
      ) : (
        <div className="space-y-2">
          <table className="w-full text-xs" data-testid="full-scan-table">
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-border/30">
                  <td className="py-1.5 pr-3 text-foreground">{r.label}</td>
                  <td className="py-1.5 pr-3">
                    {r.key === 'tenant' && !r.row
                      ? `Not set (laptops use their install value, else every ${view.defaultDays} days)`
                      : intervalText(r.row?.fullScanEveryDays)}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">
                    {r.row?.updatedAt ? `changed ${new Date(r.row.updatedAt).toLocaleDateString('en-IN')}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select aria-label="Full-scan interval for" value={scope} onChange={(e) => setScope(e.target.value)} className={INPUT}>
              <option value="tenant">Whole tenant</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1">
              Every
              <input
                aria-label="Days between full scans"
                inputMode="numeric"
                placeholder={String(view.defaultDays)}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className={`${INPUT} w-14`}
              />
              days
            </label>
            <input
              aria-label="Reason for the interval change"
              placeholder="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`${INPUT} min-w-[12rem] flex-1`}
            />
            <Button size="sm" disabled={busy} onClick={() => void save(false)}>
              {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
              Save interval
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save(true)}>
              Clear
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {view.statement.zero} {view.statement.closes}
          </p>
          {msg ? (
            <p role="alert" className="text-xs text-destructive">
              {msg}
            </p>
          ) : null}
        </div>
      )}
    </GlassCard>
  );
}
